import { ErrorMessageForField } from '../base/ErrorMessageForField';
import {
  type FC,
  type ForwardedRef,
  type PropsWithChildren,
  type ReactNode,
  useMemo,
} from 'react';
import { FormLabel } from '@components/input/FormLabel.tsx';
import { Icon } from '@components/Icon.tsx';
import { IconType } from '@src/IconType.ts';
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import {
  type ValidationMode,
  type ValidationResult,
  type ValidatorFn,
} from '@funnellab/shared/src/validation';
import { classNames } from '@utils/classNames';
import { useValidation } from '@hooks/useValidation';

declare module 'react' {
  function forwardRef<T, P>(
    render: (props: P, ref: ForwardedRef<T>) => ReactElement | null,
  ): (props: P & RefAttributes<T>) => ReactElement | null;
}

export type SelectProps<Option> = {
  readonly options: readonly Option[];
  readonly onChange: (newValue: Option) => void;
  readonly displayValue: (value: Option) => string;
  readonly value: Option;
  readonly title?: ReactNode;
  readonly infoText?: string;
  readonly required?: boolean;
  readonly validate?: ValidatorFn<Option>;
  readonly validationMode?: ValidationMode;
  readonly errorMessages?: Partial<Record<ValidationResult, string>>;
  readonly classNameContainer?: string;
  readonly disabled?: boolean;
  readonly name?: string;
  readonly selectTestId?: string;
  readonly classNameLabel?: string;
  readonly buttonTestId?: string;
  readonly passthrough?: {
    button?: { className?: string };
  };
  readonly children?: {
    option?: FC<PropsWithChildren<{ value: Option; className?: string }>>;
  };
  readonly size?: 'normal' | 'small';
  readonly showErrorMessage?: boolean;
  readonly id?: string;
};

export const Select = <T,>({
  value,
  displayValue,
  onChange,
  title,
  infoText,
  classNameContainer,
  validate: validateFn,
  validationMode,
  errorMessages = {},
  required,
  options,
  disabled = false,
  name,
  selectTestId = 'select',
  classNameLabel,
  buttonTestId,
  passthrough,
  children,
  size = 'normal',
  showErrorMessage = true,
  id,
}: SelectProps<T>) => {
  const { isValid, errorMessage, runValidation } = useValidation({
    value: value,
    validateFn: validateFn,
    required: required,
    messageKeys: errorMessages,
    validationMode,
  });

  const sizeClasses = useMemo(() => {
    if (size === 'small') {
      return 'px-2 py-1.5 rounded-lg';
    }
    return 'px-4 py-3 rounded-xl';
  }, [size]);

  return (
    <div className={classNameContainer}>
      <Listbox
        as={'div'}
        data-testid={selectTestId}
        value={value}
        onChange={(value) => {
          onChange(value);
          runValidation(value);
        }}
        disabled={!!disabled}
        name={name}
      >
        {({ open }) => (
          <>
            {title && (
              <Label className="mb-1">
                <FormLabel
                  label={title}
                  required={required}
                  valid={isValid}
                  className={classNameLabel}
                  infoText={infoText}
                />
              </Label>
            )}
            <div className="relative">
              <ListboxButton
                className={classNames(
                  sizeClasses,
                  'w-full text-slate-900 placeholder-slate-500 border-blue-100 border bg-white focus-visible:outline-blue-500 focus-visible:border-blue-500 active:border-blue-500 focus:border-blue-500',
                  'text-left cursor-pointer',
                  'disabled:cursor-not-allowed',
                  !disabled && !isValid && '!border-orange-600',
                  passthrough?.button?.className ?? '',
                )}
                data-testid={buttonTestId ?? 'select-btn'}
                id={id}
              >
                <SelectButtonContent
                  value={value}
                  displayValue={displayValue}
                  disabled={disabled}
                  children={children}
                />
              </ListboxButton>

              <Transition
                show={open}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions
                  className={`${sizeClasses} absolute z-[var(--z-index-dropdown)] w-full rounded-xl bg-white text-base drop-shadow-md border-blue-100 border focus:outline-none max-h-80 overflow-auto`}
                >
                  {options.map((option, index) => (
                    <ListboxOption
                      key={`${displayValue(option)}-${index}`}
                      data-testid={`selectOption_${displayValue(option)}`}
                      className={({ focus, selected }) =>
                        classNames(
                          sizeClasses,
                          'flex cursor-default select-none rounded-md justify-between',
                          (focus || selected) && 'bg-gray-100',
                        )
                      }
                      value={option}
                    >
                      {({ selected }) => (
                        <>
                          <DisplayOption
                            option={option}
                            displayValue={displayValue}
                            disabled={false}
                            children={children}
                          />
                          {selected && (
                            <span
                              className={classNames(
                                'inset-y-0 flex items-center',
                              )}
                            >
                              <Icon type={IconType.Check} className="h-5 w-5" />
                            </span>
                          )}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </div>
          </>
        )}
      </Listbox>

      {showErrorMessage && (
        <ErrorMessageForField
          showErrorMessage={!isValid}
          errorMessage={errorMessage}
        />
      )}
    </div>
  );
};

const DisplayOption = <Option,>({
  option,
  children,
  displayValue,
  disabled,
}: { option: Option } & Pick<
  SelectProps<Option>,
  'children' | 'disabled' | 'displayValue'
>) => {
  return children?.option ? (
    children.option({
      value: option,
      className: `${disabled ? 'text-slate-500' : ''}`,
    })
  ) : (
    <span
      className={classNames(
        'block truncate leading-snug',
        disabled && 'text-slate-500',
      )}
    >
      {displayValue(option)}
      {/* This zero-width-space prevents the height from collapsing if the value is empty */}
      &#8203;
    </span>
  );
};

const SelectButtonContent = <Option,>({
  children,
  value,
  disabled,
  displayValue,
}: Pick<
  SelectProps<Option>,
  'children' | 'value' | 'disabled' | 'displayValue'
>) => {
  return (
    <span className="flex justify-between items-center">
      <DisplayOption
        option={value}
        children={children}
        displayValue={displayValue}
        disabled={disabled}
      />
      <Icon
        type={IconType.ChevronDown}
        className={classNames(
          'pointer-events-none',
          'size-5 text-slate-900',
          disabled && '!text-slate-500',
        )}
      />
    </span>
  );
};
